import React, { useState } from 'react';
import { useTranslate } from 'nucleus-text';
import useBreakpoints from '@hooks/useBreakpoints';
import { CSSObject } from '@emotion/react';
import { Event_Info_PageQuery as EventInfoPageQuery } from '@graphql/gql/graphql';
import Blocks, { Block } from '@components/blocks/Blocks';
import { injectTestId } from '@cvent/nucleus-test-automation';
import LandingInfoContentSection from '@components/landing/LandingInfoContentSection';
import AddToCalendar from '@components/sessionDetails/AddToCalendar';
import InfoPageSessionStatus from '@components/landing/InfoPageSessionStatus';
import SpeakersCard from '@components/landing/SpeakerCardV2';
import Spacer from '@components/layout/Spacer';
import SpeakerModal from '@components/landing/SpeakerModal';
import Typography from '@cvent/blocks/components/Typography';
import { hasSpeakers } from './landingUtils';
import DateRangeSubheader from './DateRangeSubheader';
import RichTextEventDescription from './RichTextEventDescription';
import InfoPageHeader from './InfoPageHeader';

type InfoPageContentProps = {
  eventInfo: EventInfoPageQuery['eventInfo'];
  hasDescription?: boolean;
  descriptionCallToAction?: JSX.Element;
  children?: React.ReactNode[];
  now: Date;
};

type Speaker = EventInfoPageQuery['eventInfo']['speakers'][0];

const useStyles = (hasSpeaker: boolean): Record<string, CSSObject> => {
  const breakpoints = useBreakpoints();
  const { isMobile, isM, mediaIsMobile, mediaIsLargeAndAbove, mediaIsM } = breakpoints;

  const gridResponsive = {
    gridColumn: '1 / -1',
    [mediaIsLargeAndAbove]: {
      minWidth: 416,
      gridColumn: hasSpeaker ? '2 / 7' : '4 / 10'
    }
  };

  return {
    flexStartMargin: {
      marginBottom: isMobile || isM ? 20 : 0
    },
    speakerLabel: {
      margin: 0,
      marginBottom: 16
    },
    richTextDescriptionContainer: {
      padding: 0,
      ...(isM && { padding: '0px 24px' }),
      ...(isMobile && { padding: '0px 8px' }),
      ...(hasSpeaker && isMobile && { padding: '0px 24px' })
    },
    container: {
      display: 'grid',
      gridTemplateColumns: 'repeat(4, 1fr)',
      gridColumnGap: 16,
      gridTemplateAreas: `
           "header-description"
           "speaker"
          `,
      [mediaIsM]: {
        display: 'flex',
        flexDirection: 'column',
        width: 514,
        maxWidth: 514
      },
      [mediaIsLargeAndAbove]: {
        gridTemplateColumns: 'repeat(12, 1fr)',
        gridColumnGap: 24,
        gridTemplateAreas: hasSpeaker
          ? `
           "header-description speaker"
           "header-description speaker"
          `
          : `
            "header-description"
          `
      }
    },
    header: {
      marginBottom: 32,
      [mediaIsMobile]: {
        marginBottom: 24
      }
    },
    headerDescription: {
      gridArea: 'header-description',
      marginBottom: 32,
      ...gridResponsive
    },
    callToAction: {
      gridArea: 'callToAction',
      ...gridResponsive
    },
    speaker: {
      gridArea: 'speaker',
      gridColumn: '1 / -1',
      ...gridResponsive,
      [mediaIsLargeAndAbove]: {
        minWidth: 375,
        gridColumn: '8 / 12'
      }
    },
    speakerCard: {
      [mediaIsMobile]: {
        maxWidth: 400
      }
    }
  };
};

function InfoPageContent({
  eventInfo,
  hasDescription,
  descriptionCallToAction,
  children,
  now
}: InfoPageContentProps): JSX.Element {
  const [selectedSpeaker, setSelectedSpeaker] = useState<Speaker>(null);
  const [isSpeakerModalOpen, setIsSpeakerModalOpen] = useState(false);
  const { translate } = useTranslate();
  const breakpoints = useBreakpoints();
  const { isMobile } = breakpoints;
  const session = eventInfo.sessions[0];

  const { speakers, title, start: eventStart, end: eventEnd } = eventInfo;
  const isSpeakerAvailable = hasSpeakers(speakers);

  const descriptionLineCount = isMobile ? 10 : 15;

  const {
    flexStartMargin,
    speakerLabel,
    richTextDescriptionContainer,
    container,
    header,
    headerDescription,
    speaker,
    speakerCard
  } = useStyles(isSpeakerAvailable);

  const showSpeakers = hasSpeakers(speakers) ? (
    <div css={flexStartMargin}>
      <Typography css={speakerLabel} as="h2">
        {translate('LandingPage-Speakers-Label', {
          speakers: speakers.length
        })}
      </Typography>
      {speakers.map(speaker => (
        <div key={speaker.id} css={speakerCard}>
          <SpeakersCard
            speaker={speaker}
            onClick={() => {
              if (speaker.biography) {
                setSelectedSpeaker(speaker);
                setIsSpeakerModalOpen(true);
              }
            }}
          />
          <Spacer size={1 / 2} />
        </div>
      ))}
    </div>
  ) : null;

  const hasWebinarEnded = now >= new Date(eventEnd);

  const subheader =
    Boolean(eventStart) && Boolean(eventEnd) ? (
      <>
        {descriptionCallToAction}
        {hasWebinarEnded ? null : (
          <>
            <DateRangeSubheader startDate={eventStart} endDate={eventEnd} />
            <AddToCalendar
              start={eventInfo.start}
              end={eventInfo.end}
              name={eventInfo.title}
              sessionId={eventInfo.id}
              sessionDescription={eventInfo.description}
              eventName={eventInfo.title}
              showPlusIcon
              hideCalendarIcon
            />
          </>
        )}
      </>
    ) : null;

  const renderContent = (
    <Block key="main">
      <LandingInfoContentSection>
        <div css={container}>
          <div css={headerDescription}>
            <div css={header}>
              <InfoPageSessionStatus
                now={now}
                sessionStart={session.start}
                sessionEnd={session.end}
                eventEnd={eventInfo.end}
              />
              <InfoPageHeader subheader={subheader} title={title} />
            </div>
            {hasDescription ? (
              <RichTextEventDescription
                eventDescription={eventInfo.description}
                enableExpandableDescription
                numberOfLines={descriptionLineCount}
                customContainerStyle={richTextDescriptionContainer}
              />
            ) : null}
          </div>

          {/* Speaker */}
          <div css={speaker} {...injectTestId('info-page-speakers-container')}>
            {showSpeakers}
          </div>
        </div>
        {isSpeakerModalOpen && (
          <SpeakerModal
            speaker={selectedSpeaker}
            isModalOpen={isSpeakerModalOpen}
            setIsModalOpen={setIsSpeakerModalOpen}
          />
        )}
      </LandingInfoContentSection>
    </Block>
  );
  // Combine main block with passed in children
  const blocks = [
    renderContent,
    ...React.Children.map(children, (child, index) => (child != null ? <Block key={index}>{child}</Block> : null))
  ];

  return <Blocks css={{ minHeight: '100vh' }}>{blocks}</Blocks>;
}

export default InfoPageContent;
